import type { RoundStatus, SkuSummary, SupplierRoundSummary } from "@dishour/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unwrap } from "./client.js";

const get = <T,>(path: string) => fetch(path, { credentials: "same-origin" }).then(unwrap<T>);
const post = <T,>(path: string, body: unknown) =>
  fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  }).then(unwrap<T>);

export const adminRoundsKey = ["admin", "rounds"] as const;
export const skusKey = ["admin", "skus"] as const;
export const suppliersKey = ["admin", "suppliers"] as const;
export const restaurantsKey = ["admin", "restaurants"] as const;

type Org = { id: string; name: string; isActive: boolean };

export const useAdminRounds = () =>
  useQuery({ queryKey: adminRoundsKey, queryFn: () => get<SupplierRoundSummary[]>("/api/admin/rounds") });
export const useSkus = () => useQuery({ queryKey: skusKey, queryFn: () => get<SkuSummary[]>("/api/admin/skus") });
export const useSuppliers = () =>
  useQuery({ queryKey: suppliersKey, queryFn: () => get<Org[]>("/api/admin/suppliers") });
export const useRestaurants = () =>
  useQuery({ queryKey: restaurantsKey, queryFn: () => get<Org[]>("/api/admin/restaurants") });

function useInvalidating<TVars, TData>(fn: (vars: TVars) => Promise<TData>, keys: readonly unknown[][]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => keys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey })),
  });
}

export const useCreateRound = () =>
  useInvalidating((body: unknown) => post<{ id: string }>("/api/admin/rounds", body), [[...adminRoundsKey]]);

export const useUpdateRoundStatus = () =>
  useInvalidating(
    (vars: { id: string; status: RoundStatus }) =>
      post<{ id: string; status: RoundStatus }>(`/api/admin/rounds/${vars.id}/status`, { status: vars.status }),
    [[...adminRoundsKey]],
  );

export const useCreateSku = () =>
  useInvalidating((body: unknown) => post<SkuSummary>("/api/admin/skus", body), [[...skusKey]]);

export const useCreateRestaurant = () =>
  useInvalidating((body: unknown) => post<Org>("/api/admin/restaurants", body), [[...restaurantsKey]]);

export const useCreateSupplier = () =>
  useInvalidating((body: unknown) => post<Org>("/api/admin/suppliers", body), [[...suppliersKey], [...skusKey]]);

export const useCreateInvite = () =>
  useMutation({
    mutationFn: (body: unknown) => post<{ inviteUrl: string; expiresAt: string }>("/api/admin/invites", body),
  });

export const connectedSuppliersKey = (restaurantId: string) =>
  ["admin", "restaurant-suppliers", restaurantId] as const;

export const useConnectedSuppliers = (restaurantId: string) =>
  useQuery({
    queryKey: connectedSuppliersKey(restaurantId),
    queryFn: () => get<string[]>(`/api/admin/restaurants/${restaurantId}/suppliers`),
  });

function useSupplierConnectionMutation(method: "PUT" | "DELETE") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { restaurantId: string; supplierId: string }) =>
      fetch(`/api/admin/restaurants/${vars.restaurantId}/suppliers/${vars.supplierId}`, {
        method,
        credentials: "same-origin",
      }).then(unwrap<{ restaurantId: string; supplierId: string }>),
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: connectedSuppliersKey(vars.restaurantId) }),
  });
}

export const useConnectSupplier = () => useSupplierConnectionMutation("PUT");
export const useDisconnectSupplier = () => useSupplierConnectionMutation("DELETE");
